/**
 * Оргструктура для модалки «Права пользователей».
 *
 * Модель в UI:
 * - «Доступ к отделам» (галочки ORG_DEPARTMENTS) — отделы пользователя и видимость кнопок сайдбара
 * - «Должность» — JOB_TITLES / произвольный текст
 * - «Роль» — уровень прав (user / hr / admin …)
 *
 * Поле users.department остаётся в БД для совместимости: при выборе галочек
 * синхронизируется с первым отмеченным орг-отделом. Старые значения-должности
 * в department не сбрасываем принудительно (видимость сайдбара).
 */
export const ORG_DEPARTMENTS = [
  'HR',
  'ТОП',
  'Маркетинг',
  'КЦ',
  'Отдел качества',
  'Соц.сети',
  'Финансовый отдел',
  'Отдел логистики',
  'Складской отдел',
  'Отдел закупок',
  'ОТК',
];

/**
 * Исторические значения «департамента» = по сути должности.
 * Используются в колонке «Должность» и для совместимости с видимостью сайдбара.
 */
export const JOB_TITLES = [
  'Организатор фотосессий',
  'Супервайзер колл-центра',
  'Менеджер отдела возвратов',
  'Менеджер склада',
  'Оператор колл-центра',
  'Менеджер отдела продаж',
  'Финансовый менеджер',
  'ОТК (отдел технического контроля)',
  'SMM-менеджер',
  'Водитель',
  'Менеджер по дропшиппингу',
  'Менеджер по качеству обслуживания клиентов',
  'Менеджер маркетплейса',
  'Бухгалтер по первичной документации',
  'Руководитель отдела закупок',
  'Супервайзер отдела возвратов',
  'CEO (генеральный директор)',
  'Контент-менеджер',
  'Завсклад (заведующий складом)',
  'Системный администратор',
  'Владелец компании',
  'Помощник SMM-менеджера',
  'Руководитель отдела логистики',
  'HR-менеджер (менеджер по персоналу)',
  'Менеджер отдела маркетинга',
  'Менеджер отдела закупок',
  'CFO (финансовый директор)',
  'Оператор-учётчик',
];

/** Подсказка: какая должность обычно относится к какому отделу (не применяется автоматически). */
export const JOB_TITLE_TO_ORG_DEPARTMENT = {
  'HR-менеджер (менеджер по персоналу)': 'HR',
  'CEO (генеральный директор)': 'ТОП',
  'Владелец компании': 'ТОП',
  'Системный администратор': 'ТОП',
  'Менеджер отдела маркетинга': 'Маркетинг',
  'Менеджер маркетплейса': 'Маркетинг',
  'Менеджер по дропшиппингу': 'Маркетинг',
  'Организатор фотосессий': 'Маркетинг',
  'Контент-менеджер': 'Маркетинг',
  'Супервайзер колл-центра': 'КЦ',
  'Оператор колл-центра': 'КЦ',
  'Менеджер по качеству обслуживания клиентов': 'Отдел качества',
  'Менеджер отдела возвратов': 'Отдел качества',
  'Супервайзер отдела возвратов': 'Отдел качества',
  'SMM-менеджер': 'Соц.сети',
  'Помощник SMM-менеджера': 'Соц.сети',
  'Финансовый менеджер': 'Финансовый отдел',
  'CFO (финансовый директор)': 'Финансовый отдел',
  'Бухгалтер по первичной документации': 'Финансовый отдел',
  'Оператор-учётчик': 'Финансовый отдел',
  'Руководитель отдела логистики': 'Отдел логистики',
  'Водитель': 'Отдел логистики',
  'Менеджер склада': 'Складской отдел',
  'Завсклад (заведующий складом)': 'Складской отдел',
  'Руководитель отдела закупок': 'Отдел закупок',
  'Менеджер отдела закупок': 'Отдел закупок',
  'Менеджер отдела продаж': 'Отдел закупок',
  'ОТК (отдел технического контроля)': 'ОТК',
};

export function normalizeDeptLabel(value) {
  return String(value || '').trim();
}

/** Все метки пользователя, по которым может срабатывать видимость сайдбара. */
export function collectUserVisibilityLabels(user) {
  if (!user) return [];
  const out = [];
  const push = (v) => {
    const s = normalizeDeptLabel(v);
    if (s) out.push(s);
  };
  push(user.department);
  push(user.position);
  // position иногда хранят через « / » или запятую
  String(user.position || '')
    .split(/[,;/|]+/)
    .forEach((part) => push(part));
  const access = Array.isArray(user.departments_access) ? user.departments_access : [];
  access.forEach(push);
  return [...new Set(out)];
}

/** Совпадает ли пользователь с настройкой видимости (массив отделов/должностей). */
export function userMatchesVisibilityList(user, visibilitySetting) {
  if (!Array.isArray(visibilitySetting)) return true;
  if (visibilitySetting.length === 0) return false;
  const labels = collectUserVisibilityLabels(user).map((s) => s.toLowerCase());
  return visibilitySetting.some((allowed) => {
    const a = normalizeDeptLabel(allowed).toLowerCase();
    return a && labels.includes(a);
  });
}

/** Список значений для «Доступ к отделам»: сначала орг-отделы, потом редкие legacy. */
export function buildAccessDepartmentOptions(extra = []) {
  const seen = new Set();
  const out = [];
  [...ORG_DEPARTMENTS, ...extra].forEach((d) => {
    const s = normalizeDeptLabel(d);
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  });
  return out;
}

export function isOrgDepartment(value) {
  const key = normalizeDeptLabel(value).toLowerCase();
  if (!key) return false;
  return ORG_DEPARTMENTS.some((o) => o.toLowerCase() === key);
}

/** Первый орг-отдел из галочек доступа (или пустая строка). */
export function resolvePrimaryOrgDepartment(accessList) {
  const list = Array.isArray(accessList) ? accessList : [];
  const hit = list.find((d) => isOrgDepartment(d));
  return hit ? normalizeDeptLabel(hit) : '';
}

/**
 * Что записать в users.department после изменения галочек:
 * - есть орг-отделы в доступе → первый из них
 * - доступа нет, а в department всё ещё орг-отдел → очистить (иначе ВПР/Канбан «залипают»)
 * - иначе null = не трогать (legacy-должность в department)
 */
export function nextDepartmentAfterAccessChange(accessList, currentDepartment) {
  const primary = resolvePrimaryOrgDepartment(accessList);
  if (primary) return primary;
  if (isOrgDepartment(currentDepartment)) return '';
  return null;
}

/** Строки под аватаром: должность и отдел отдельно (чтобы длинный текст не сжимался в «…»). */
export function formatUserOrgLines(user) {
  if (!user) return [];
  const position = normalizeDeptLabel(user.position);
  const department = normalizeDeptLabel(user.department);
  const lines = [];
  if (position) lines.push(position);
  if (department && department.toLowerCase() !== position.toLowerCase()) lines.push(department);
  return lines;
}

/** Подпись под аватаром: должность · отдел (пустые части пропускаются). */
export function formatUserOrgSubtitle(user) {
  return formatUserOrgLines(user).join(' · ');
}

/**
 * Эффективный список отделов для галочек:
 * departments_access + орг-отдел из department + маппинг старых должностей → орг-отдел.
 */
export function effectiveOrgAccessList(userOrAccess, department, position) {
  const access = Array.isArray(userOrAccess)
    ? userOrAccess
    : (Array.isArray(userOrAccess?.departments_access) ? userOrAccess.departments_access : []);
  const dept = normalizeDeptLabel(
    department != null ? department : (userOrAccess && !Array.isArray(userOrAccess) ? userOrAccess.department : '')
  );
  const pos = normalizeDeptLabel(
    position != null ? position : (userOrAccess && !Array.isArray(userOrAccess) ? userOrAccess.position : '')
  );

  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const s = normalizeDeptLabel(raw);
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };

  access.forEach(push);
  if (isOrgDepartment(dept)) push(dept);

  // Старые значения-должности в access/department → добавить соответствующий орг-отдел
  [...access, dept, pos].forEach((label) => {
    const mapped = JOB_TITLE_TO_ORG_DEPARTMENT[normalizeDeptLabel(label)];
    if (mapped) push(mapped);
  });

  return out;
}

export function accessListIncludes(accessList, departmentName) {
  const want = normalizeDeptLabel(departmentName).toLowerCase();
  if (!want) return false;
  return (accessList || []).some((a) => normalizeDeptLabel(a).toLowerCase() === want);
}
